import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Calendar,
  Clock,
  MapPin,
  Users,
  Trophy,
  Medal,
  ArrowLeft,
  Gamepad2,
  Crosshair,
} from "lucide-react";
import api, { LOGO_WARZONE } from "@/lib/api";
import { IMG } from "@/lib/assets";

function fmt(iso) {
  if (!iso) return "Da definire";
  return new Date(iso).toLocaleString("it-IT", {
    day: "2-digit",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const STATE_LABEL = {
  draft: "Bozza",
  registration_open: "Iscrizioni Aperte",
  registration_closed: "Iscrizioni Chiuse",
  lobby_generated: "Lobby Generate",
  in_progress: "In Corso",
  completed: "Completato",
  archived: "Archiviato",
};

function RankPosition({ rank }) {
  if (rank > 3) {
    return (
      <span className="font-oswald text-gray-400">
        {rank}
      </span>
    );
  }

  return (
    <Medal
      className={`w-5 h-5 ${
        rank === 1
          ? "text-[#D4AF37]"
          : rank === 2
            ? "text-gray-300"
            : "text-[#997a1a]"
      }`}
    />
  );
}

export default function TorneoDetail() {
  const { id } = useParams();

  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [rankingMode, setRankingMode] = useState("teams");
  const [lobbyId, setLobbyId] = useState("");

  useEffect(() => {
    let cancelled = false;
    let loading = false;
    let live = false;

    setErr(false);
    setData(null);
    setLobbyId("");
    setRankingMode("teams");

    const refresh = async (initial = false) => {
      if (loading) return;
      loading = true;

      try {
        const response = await api.get(`/public/tournaments/${id}`);

        if (!cancelled) {
          setData(response.data);
          live = response.data?.tournament?.state === "in_progress";
          setErr(false);
        }
      } catch {
        if (!cancelled && initial) {
          setErr(true);
        }
      } finally {
        loading = false;
      }
    };

    refresh(true);

    const interval = setInterval(() => {
      if (live) refresh();
    }, 30000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [id]);

  if (err) {
    return (
      <div className="text-center py-32 text-white font-rajdhani">
        Torneo non trovato.{" "}
        <Link to="/tornei" className="cyber-text underline">
          Torna ai tornei
        </Link>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-32 text-gray-400 font-rajdhani">
        Caricamento...
      </div>
    );
  }

  const t = data.tournament;
  const concluded = ["completed", "archived"].includes(t.state);
  const live = t.state === "in_progress";

  const selectedLobby = lobbyId
    ? (data.lobbies || []).find((lobby) => lobby.id === lobbyId)
    : null;

  const teamRanking = selectedLobby
    ? selectedLobby.ranking || []
    : data.ranking || [];

  const mvpRanking = selectedLobby
    ? selectedLobby.mvp_ranking || []
    : data.mvp_ranking || [];

  const scopeLabel = selectedLobby ? selectedLobby.name : "Generale";

  return (
    <div className="text-white">
      <section className="relative py-20 overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url(${IMG.cyberBg})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 to-[#050507]" />
        <div className="absolute inset-0 grid-overlay opacity-30" />

        <div className="relative max-w-6xl mx-auto px-5">
          <Link
            to="/tornei"
            className="inline-flex items-center gap-2 font-rajdhani text-gray-300 hover:text-[#67e8f9] text-sm mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            Tutti i tornei
          </Link>

          <div className="flex flex-col md:flex-row items-center gap-8">
            <img
              src={LOGO_WARZONE}
              alt=""
              className="w-32 h-32 object-contain floaty drop-shadow-[0_0_40px_rgba(34,211,238,0.5)]"
            />

            <div className="text-center md:text-left">
              <span
                className={`font-rajdhani uppercase tracking-wider text-xs px-3 py-1 clip-beveled-sm ${
                  live
                    ? "bg-[#10b981] text-black"
                    : "bg-[#22d3ee] text-black"
                }`}
              >
                {STATE_LABEL[t.state] || t.state}
              </span>

              <h1 className="heading text-5xl sm:text-6xl cyber-text mt-3">
                {t.name}
              </h1>

              {t.subtitle && (
                <p className="heading text-2xl text-gray-300">
                  {t.subtitle}
                </p>
              )}
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-5 py-14 space-y-12">
        {/* INFO */}
        <section
          className="grid grid-cols-2 md:grid-cols-4 gap-4"
          data-testid="detail-info"
        >
          {[
            [Gamepad2, "Modalità", t.mode],
            [MapPin, "Mappa", t.map],
            [Calendar, "Data", fmt(t.start_at).split(",")[0]],
            [Clock, "Orario", fmt(t.start_at).split(",")[1] || "--"],
            [Users, "Squadre", `${t.teams}/${t.max_teams}`],
            [Trophy, "Partite", t.num_matches],
            [Crosshair, "Formato", `${t.min_players}v${t.min_players}`],
            [Trophy, "Disputate", data.matches_played],
          ].map(([Ic, label, value], i) => (
            <div key={i} className="cyber-panel clip-hud p-4">
              <Ic
                className="w-6 h-6 text-[#67e8f9] mb-2"
                strokeWidth={1.5}
              />
              <div className="font-oswald text-xl text-white">
                {value}
              </div>
              <div className="font-rajdhani text-[10px] uppercase tracking-widest text-gray-400">
                {label}
              </div>
            </div>
          ))}
        </section>

        {/* WINNER */}
        {concluded && data.winner && (
          <section
            className="panel panel-gold clip-beveled p-8 text-center"
            data-testid="detail-winner"
          >
            <Trophy className="w-12 h-12 text-[var(--gold)] mx-auto mb-3" />
            <p className="font-rajdhani uppercase tracking-[0.3em] text-[var(--gold-light)] text-xs">
              Vincitore
            </p>
            <h2 className="heading text-5xl gold-text mt-1">
              {data.winner.name}
            </h2>
            <p className="font-oswald text-white text-xl mt-1">
              {data.winner.points} punti
            </p>
          </section>
        )}

        {/* CLASSIFICHE */}
        <section data-testid="detail-ranking">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5 mb-5">
            <div>
              <h2 className="heading text-4xl text-white">
                {concluded ? "Classifiche Finali" : "Classifiche"}
              </h2>
              <p className="font-rajdhani text-gray-400 text-sm mt-1">
                Squadre e MVP · Generale e singole lobby
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex border border-[#22d3ee]/30 bg-black/40">
                <button
                  type="button"
                  onClick={() => setRankingMode("teams")}
                  className={`px-5 py-2 font-rajdhani uppercase tracking-wide ${
                    rankingMode === "teams"
                      ? "bg-[#22d3ee] text-black"
                      : "text-gray-300 hover:text-white"
                  }`}
                >
                  Squadre
                </button>

                <button
                  type="button"
                  onClick={() => setRankingMode("mvp")}
                  className={`px-5 py-2 font-rajdhani uppercase tracking-wide ${
                    rankingMode === "mvp"
                      ? "bg-[#D4AF37] text-black"
                      : "text-gray-300 hover:text-white"
                  }`}
                >
                  MVP
                </button>
              </div>

              <select
                value={lobbyId}
                onChange={(event) => setLobbyId(event.target.value)}
                aria-label="Seleziona classifica lobby"
                className="bg-[#0a0a0a] border border-[#22d3ee]/30 px-4 py-2 text-white font-rajdhani focus:outline-none focus:border-[#67e8f9]"
              >
                <option value="">Generale</option>

                {(data.lobbies || []).map((lobby) => (
                  <option key={lobby.id} value={lobby.id}>
                    {lobby.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="mb-3 font-rajdhani uppercase tracking-[0.2em] text-xs text-[#67e8f9]">
            {rankingMode === "teams"
              ? "Classifica Squadre"
              : "Classifica MVP"}{" "}
            · {scopeLabel}
          </div>

          {rankingMode === "teams" ? (
            teamRanking.length ? (
              <div className="cyber-panel clip-hud overflow-x-auto">
                <table className="w-full min-w-[560px]">
                  <thead>
                    <tr className="bg-black/50 border-b border-[#22d3ee]/30 font-teko text-lg text-[#67e8f9] tracking-wide">
                      <th className="text-left px-4 py-3 w-16">Pos</th>
                      <th className="text-left px-4 py-3">Squadra</th>
                      <th className="text-right px-4 py-3">Kill</th>
                      <th className="text-right px-4 py-3">Vittorie</th>
                      <th className="text-right px-4 py-3">Punti</th>
                    </tr>
                  </thead>

                  <tbody>
                    {teamRanking.map((row) => (
                      <tr
                        key={row.team_id}
                        className={`border-b border-white/5 font-rajdhani ${
                          row.rank <= 3 ? "bg-white/5" : ""
                        }`}
                      >
                        <td className="px-4 py-3">
                          <RankPosition rank={row.rank} />
                        </td>

                        <td className="px-4 py-3 text-white">
                          {row.name}
                          {row.tag && (
                            <span className="text-[#67e8f9] text-xs ml-1">
                              ({row.tag})
                            </span>
                          )}
                        </td>

                        <td className="px-4 py-3 text-right font-oswald">
                          {row.kills}
                        </td>

                        <td className="px-4 py-3 text-right font-oswald">
                          {row.wins}
                        </td>

                        <td className="px-4 py-3 text-right font-oswald font-bold text-[#67e8f9] text-xl">
                          {row.points}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="font-rajdhani text-gray-500">
                Nessun risultato pubblicato al momento.
              </p>
            )
          ) : mvpRanking.length ? (
            <div className="cyber-panel clip-hud overflow-x-auto">
              <table className="w-full min-w-[560px]">
                <thead>
                  <tr className="bg-black/50 border-b border-[#D4AF37]/30 font-teko text-lg text-[#D4AF37] tracking-wide">
                    <th className="text-left px-4 py-3 w-16">Pos</th>
                    <th className="text-left px-4 py-3">Player</th>
                    <th className="text-left px-4 py-3">Squadra</th>
                    <th className="text-right px-4 py-3">Kill</th>
                    <th className="text-right px-4 py-3">Punti MVP</th>
                  </tr>
                </thead>

                <tbody>
                  {mvpRanking.map((row) => (
                    <tr
                      key={row.player_id}
                      className={`border-b border-white/5 font-rajdhani ${
                        row.rank <= 3 ? "bg-white/5" : ""
                      }`}
                    >
                      <td className="px-4 py-3">
                        <RankPosition rank={row.rank} />
                      </td>

                      <td className="px-4 py-3 text-white">
                        {row.name}
                      </td>

                      <td className="px-4 py-3 text-gray-300">
                        {row.team_name}
                        {row.team_tag && (
                          <span className="text-[#D4AF37] text-xs ml-1">
                            ({row.team_tag})
                          </span>
                        )}
                      </td>

                      <td className="px-4 py-3 text-right font-oswald">
                        {row.kills}
                      </td>

                      <td className="px-4 py-3 text-right font-oswald font-bold text-[#D4AF37] text-xl">
                        {row.points}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="font-rajdhani text-gray-500">
              Nessun dato MVP disponibile al momento.
            </p>
          )}
        </section>

        {/* SQUADRE */}
        <section data-testid="detail-teams">
          <h2 className="heading text-4xl text-white mb-5">
            Squadre Partecipanti
          </h2>

          {data.teams.length ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {data.teams.map((team) => (
                <div
                  key={team.id}
                  className="cyber-panel clip-hud p-4 flex items-center justify-between"
                >
                  <div>
                    <div className="heading text-xl text-white">
                      {team.name}
                    </div>

                    {team.tag && (
                      <span className="font-rajdhani text-[#67e8f9] text-xs">
                        [{team.tag}]
                      </span>
                    )}
                  </div>

                  <div className="text-right font-oswald">
                    <div className="text-[#67e8f9] text-xl">
                      {team.players}
                    </div>
                    <div className="font-rajdhani text-[10px] text-gray-400 uppercase">
                      Giocatori
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="font-rajdhani text-gray-500">
              Nessuna squadra iscritta.
            </p>
          )}
        </section>
      </div>
    </div>
  );
}
